import { syntaxTree } from "@codemirror/language";
import { type Extension } from "@codemirror/state";
import { EditorView, ViewPlugin, type ViewUpdate } from "@codemirror/view";

/** Making a rendered table EDITABLE rather than merely readable.
 *
 *  A table is the one construct people build visually (the toolbar's grid
 *  picker) and then have to maintain as raw pipes, which is where the whole
 *  affordance falls down: clicking the rendered grid used to drop the caret at
 *  the block's edge, so you landed at the start of the table and had to count
 *  pipes to reach the cell you were looking at. These two pieces close that:
 *  `cellSpans` maps a rendered cell back to its source text so a click can land
 *  IN it, and `formatTable` keeps the raw pipes in a readable grid so the source
 *  is legible when it does unfold. */

/** Every cell's source range within one table row, relative to the row's start.
 *  `\|` is an escaped pipe (content, not a separator) and the optional outer
 *  pipes are fences, so both are skipped the same way `splitTableRow` does —
 *  the two must agree or a click would target the wrong cell. */
export function rowCellSpans(text: string): { from: number; to: number }[] {
  const spans: { from: number; to: number }[] = [];
  let start = 0;
  let sawPipe = false;
  for (let i = 0; i <= text.length; i++) {
    const ch = text[i];
    if (ch === "\\" && text[i + 1] === "|") {
      i++;
      continue;
    }
    if (ch === "|" || i === text.length) {
      if (!(i === 0)) spans.push({ from: start, to: i });
      if (ch === "|") sawPipe = true;
      start = i + 1;
    }
  }
  // A trailing fence pipe produces one empty span past the last real cell.
  if (sawPipe && spans.length && text.trimEnd().endsWith("|")) spans.pop();
  return spans;
}

/** Per-cell source ranges for a whole table, indexed [visualRow][col] where row
 *  0 is the header and the delimiter row is skipped — the same shape the widget
 *  renders, so a cell element's coordinates index straight into it. Offsets are
 *  relative to the table's first character. */
export function cellSpans(source: string): { from: number; to: number }[][] {
  const out: { from: number; to: number }[][] = [];
  let offset = 0;
  source.split("\n").forEach((line, i) => {
    if (i !== 1) {
      // Trim inside each span so the caret lands on the TEXT, not the padding.
      out.push(
        rowCellSpans(line).map(({ from, to }) => {
          const raw = line.slice(from, to);
          const lead = raw.length - raw.trimStart().length;
          const trail = raw.length - raw.trimEnd().length;
          return { from: offset + from + lead, to: offset + to - trail };
        }),
      );
    }
    offset += line.length + 1; // +1 for the newline
  });
  return out;
}

const DELIMITER_CELL = /^:?-+:?$/;

/** Is this the `| --- | :-: |` line that turns the row above it into a table?
 *  It is the only unambiguous marker a GFM table has — the header row is just a
 *  line with pipes in it — so finding a table means finding one of these. */
export function isDelimiterRow(text: string): boolean {
  if (!text.includes("-")) return false;
  const cells = rowCellSpans(text).map(({ from, to }) => text.slice(from, to).trim());
  return cells.length > 0 && cells.every((c) => DELIMITER_CELL.test(c));
}

/** Re-pad a table's source so its columns line up, or null when it is already
 *  aligned / is not a table this can safely touch.
 *
 *  Only WHITESPACE inside cells changes — never the content, never the column
 *  count, never the alignment colons. A ragged row is padded out to the header's
 *  width so the grid stays rectangular instead of the last column running off
 *  the end of one line and not the next. */
export function formatTable(source: string): string | null {
  const lines = source.split("\n");
  if (lines.length < 2) return null;
  const rows = lines.map((l) => rowCellSpans(l).map(({ from, to }) => l.slice(from, to).trim()));
  const delim = rows[1];
  if (!delim?.length || !delim.every((c) => DELIMITER_CELL.test(c))) return null;
  const cols = Math.max(...rows.map((r) => r.length));
  if (!Number.isFinite(cols) || cols === 0) return null;
  // A column is as wide as its widest cell, and at least 3 so the delimiter row
  // can still spell `---` (and `:-:` keeps both colons).
  const width: number[] = [];
  for (let c = 0; c < cols; c++) {
    width[c] = Math.max(3, ...rows.map((r) => (r[c] ?? "").length));
  }
  const out = rows.map((cells, i) => {
    const padded = Array.from({ length: cols }, (_, c) => {
      const text = cells[c] ?? "";
      const w = width[c] ?? 3;
      if (i !== 1) return ` ${text.padEnd(w)} `;
      // Delimiter row: keep the colons, fill the middle with dashes.
      const left = text.startsWith(":");
      const right = text.endsWith(":");
      const dashes = "-".repeat(Math.max(1, w - (left ? 1 : 0) - (right ? 1 : 0)));
      return ` ${left ? ":" : ""}${dashes}${right ? ":" : ""} `;
    });
    return `|${padded.join("|")}|`;
  });
  const next = out.join("\n");
  return next === source ? null : next;
}

/** The table under the caret, if any. */
function tableAt(view: EditorView, pos: number): { from: number; to: number } | null {
  for (let n = syntaxTree(view.state).resolveInner(pos, -1); n; n = n.parent!) {
    if (n.name === "Table") {
      const first = view.state.doc.lineAt(n.from);
      const last = view.state.doc.lineAt(n.to);
      return { from: first.from, to: last.to };
    }
    if (!n.parent) break;
  }
  return null;
}

/** Tidy a table's pipes once the caret LEAVES it.
 *
 *  On leaving, not on every keystroke: re-padding while someone is typing in a
 *  cell would shift the text under their cursor on every character. Waiting
 *  until they step out means the source they come back to is always a readable
 *  grid, and the edit is a single undo step. */
const autoAlignPlugin = ViewPlugin.fromClass(
  class {
    /** The table the caret was in on the previous update. */
    private inside: { from: number; to: number } | null = null;

    constructor(view: EditorView) {
      this.inside = tableAt(view, view.state.selection.main.head);
    }

    update(u: ViewUpdate) {
      if (!u.selectionSet && !u.docChanged) return;
      const head = u.state.selection.main.head;
      const now = tableAt(u.view, head);
      const left = this.inside;
      this.inside = now;
      if (!left) return;
      // Still in the same table (its bounds move as it is edited) — nothing yet.
      if (now && now.from === left.from) return;
      // The table may have shifted or vanished under an edit; re-derive from the
      // remembered start rather than trusting stale coordinates.
      if (left.from >= u.state.doc.length) return;
      const found = tableAt(u.view, left.from + 1);
      if (!found) return;
      const source = u.state.sliceDoc(found.from, found.to);
      const next = formatTable(source);
      if (next == null) return;
      // Queued out of the update cycle: dispatching from inside `update` is not
      // allowed, and this is cosmetic enough to ride the next frame.
      queueMicrotask(() => {
        const still = u.view.state.sliceDoc(found.from, found.to);
        if (still !== source) return; // moved on — skip rather than clobber
        u.view.dispatch({
          changes: { from: found.from, to: found.to, insert: next },
          userEvent: "format.table",
        });
      });
    }
  },
);

/** Auto-alignment of a table's raw pipes, applied when the caret steps out. */
export const autoAlignTables: Extension = autoAlignPlugin;
